import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { updateCollaborator as updateCollaboratorAPI } from '../services/api';
import { CollaboratorEntity, CreateCollaboratorRequest, UpdateCollaboratorRequest } from '../types';
import { PrimaryButton, SecondaryButton } from './buttons';

interface CollaboratorFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCollaboratorRequest) => Promise<void>;
  initialData?: CollaboratorEntity;
  title: string;
  isEdit?: boolean;
}

const CollaboratorForm: React.FC<CollaboratorFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  isEdit = false
}) => {
  const [formData, setFormData] = useState<CreateCollaboratorRequest>({
    name: '',
    email: '',
    ipiNumber: '',
    pro: ''
  });
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        email: initialData.email || '',
        ipiNumber: initialData.ipiNumber || '',
        pro: initialData.pro || ''
      });
      // If editing, use the existing accountId
      setSelectedAccountId(initialData.accountId || null);
    } else {
      setFormData({
        name: '',
        email: '',
        ipiNumber: '',
        pro: ''
      });
      setSelectedAccountId(null);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation (optional but must be valid if provided)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // IPI Number validation (optional but must be 9-11 digits if provided)
    if (formData.ipiNumber && formData.ipiNumber.trim()) {
      const ipiRegex = /^\d{9,11}$/;
      if (!ipiRegex.test(formData.ipiNumber.trim())) {
        newErrors.ipiNumber = 'IPI number must be 9-11 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent forms

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Clean up the data
      const cleanData: CreateCollaboratorRequest = {
        name: formData.name.trim(),
        email: formData.email?.trim() || undefined,
        ipiNumber: formData.ipiNumber?.trim() || undefined,
        pro: formData.pro?.trim() || undefined,
        accountId: selectedAccountId || undefined
      };

      if (isEdit && initialData) {
        // Update existing collaborator
        const updateData: UpdateCollaboratorRequest = cleanData;
        await updateCollaboratorAPI(initialData.id, updateData);
      } else {
        // Create new collaborator
        await onSubmit(cleanData);
      }
      
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      // Display error message to user
      const errorMessage = error?.message || 'Failed to save collaborator. Please try again.';
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateCollaboratorRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  // Use portal to render modal outside of any parent form
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface-secondary rounded-lg p-6 w-full max-w-md mx-4 border border-border-subtle">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-content flex items-center">
            <svg className="w-5 h-5 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-accent hover:text-content transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              Name <span className="text-state-error">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isLoading}
              className={`w-full px-4 py-3 bg-surface-tertiary border rounded-lg text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 ${
                errors.name ? 'border-state-error' : 'border-border-subtle'
              }`}
              placeholder="Enter collaborator's full name"
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-state-error flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.name}
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              Email <span className="text-content-muted">(optional)</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={isLoading}
              className={`w-full px-4 py-3 bg-surface-tertiary border rounded-lg text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 ${
                errors.email ? 'border-state-error' : 'border-border-subtle'
              }`}
              placeholder="collaborator@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-state-error flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.email}
              </p>
            )}
            <p className="mt-1 text-xs text-content-muted">
              Email is required for sending split invites and signatures
            </p>
          </div>

          {/* IPI Number Field */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              IPI Number <span className="text-content-muted">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.ipiNumber}
              onChange={(e) => handleInputChange('ipiNumber', e.target.value)}
              disabled={isLoading}
              className={`w-full px-4 py-3 bg-surface-tertiary border rounded-lg text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 ${
                errors.ipiNumber ? 'border-state-error' : 'border-border-subtle'
              }`}
              placeholder="123456789"
              pattern="[0-9]{9,11}"
              title="IPI number should be 9-11 digits"
            />
            {errors.ipiNumber && (
              <p className="mt-1 text-sm text-state-error flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.ipiNumber}
              </p>
            )}
            <p className="mt-1 text-xs text-content-muted">
              International identification number for music industry professionals (9-11 digits)
            </p>
          </div>

          {/* PRO Field */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">
              PRO <span className="text-content-muted">(optional)</span>
            </label>
            <select
              value={formData.pro || ''}
              onChange={(e) => handleInputChange('pro', e.target.value)}
              disabled={isLoading}
              className="w-full px-4 py-3 bg-surface-tertiary border border-border-subtle rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50"
            >
              <option value="">Select PRO...</option>
              <option value="ASCAP">ASCAP (USA)</option>
              <option value="BMI">BMI (USA)</option>
              <option value="SESAC">SESAC (USA)</option>
              <option value="GMR">GMR (USA)</option>
              <option value="PRS">PRS for Music (UK)</option>
              <option value="MCPS">MCPS (UK)</option>
              <option value="SOCAN">SOCAN (Canada)</option>
              <option value="APRA">APRA AMCOS (Australia)</option>
              <option value="GEMA">GEMA (Germany)</option>
              <option value="SACEM">SACEM (France)</option>
              <option value="SIAE">SIAE (Italy)</option>
              <option value="SGAE">SGAE (Spain)</option>
              <option value="JASRAC">JASRAC (Japan)</option>
              <option value="Other">Other</option>
            </select>
            <p className="mt-1 text-xs text-content-muted">
              Performing Rights Organization the collaborator is registered with
            </p>
          </div>

          {/* Submit Error Display */}
          {errors.submit && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-300 text-sm flex items-center">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.submit}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={isLoading || !formData.name.trim()}
              loading={isLoading}
              className="flex-1"
            >
              {isEdit ? 'Update Collaborator' : 'Create Collaborator'}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CollaboratorForm;
